
import { Injectable } from '@angular/core';

import { BehaviorSubject, map, Observable, Subject, switchMap, tap } from 'rxjs';

import { IAreaService } from '../../interfaces/IAreaService';
import { WebAccessService } from '../web.access/web-access.service';
import { AreaListFilterRequest } from '../../models/remote.request/AreaListFilterRequest';
import { Area } from '../../models/domain/Area';
import { Point } from '../../models/domain/Point';
import { AreaDetailsRequest } from '../../models/remote.request/AreaDetailsRequest';
import { CustomError } from '../../models/other/CustomError.model';

@Injectable()
export class AreaService implements IAreaService {

    private areaListSubject: BehaviorSubject<Area[]> = new BehaviorSubject<Area[]>([]);
    private filterRequestSubject: Subject<AreaListFilterRequest> = new Subject<AreaListFilterRequest>();

    constructor(
        private $webAccess: WebAccessService
    ) {
        // Build event list stream
        this.filterRequestSubject.pipe(
            switchMap(filter => this.$webAccess.GetAreaListByFilter(filter)),
            map(response => response.Result as any[]),
            tap(result => {
                for (const area of result) {
                    switch (area.Tp) {
                        case 5:
                            area.Tp = 1;
                            break;
                        case 3:
                            area.Tp = 2;
                            break;
                        default:
                            area.Tp = 3;
                            break;
                    }
                    area.Coords = (area.P || []).map((p: any) =>
                        <Point>{
                            Lat: p.Lat.toString().replace(',', '.'),
                            Lon: p.Lon.toString().replace(',', '.')
                        });
                }
            }),
            map(result => result as Area[]))
            .subscribe(events => {
                this.areaListSubject.next(events);
            });
    }

    public getAreaList$(): Observable<Area[]> {
        return this.areaListSubject.asObservable();
    }

    public findArea$(filter: AreaListFilterRequest): void {
        this.areaListSubject.next([]); //TODO GB - był null
        this.filterRequestSubject.next(filter);
    }

    public getAreaDetails$(areaId: number): Observable<Area> {
        return this.$webAccess.GetAreaDetails(areaId).pipe(
            map(result => <Area>result.Result),
            tap(result => {
                result = this.objectToModel(result);
            }));
    }

    public notifyDrawingChanges$(area: Area): Observable<void> {
        throw new CustomError('Method not implemented.');
    }

    public prepareAreaDetails$(areaId: number): Observable<Area> {
        throw new CustomError('Method not implemented.');
    }


    public deleteAreaDetails$(areaId: number): Observable<void> {
        return this.$webAccess.DeleteAreaDetails(areaId).pipe(map(result => { }));
    }

    public saveAreaDetails$(area: Area): Observable<Area> {

        const sub: Subject<Area> = new Subject();

        let areaType = area.Tp;
        switch (areaType) {
            case 1:
                areaType = 5;
                break;
            case 2:
                areaType = 3;
                break;
            default:
                areaType = 4;
                break;
        }

        let counter = 1;
        const coords = area.Coords.map(c => <any>{
            'Lat': c.Lat, 'Lon': c.Lon, Id: counter++
        });

        const request = <AreaDetailsRequest>{
            AreaName: area.Nm,
            AreaPoints: coords,
            AreaTags: area.Tg,
            AreaThickness: area.Th,
            AreaType: areaType,
            Color: area.Cl,
        };

        if (area.Id) {
            request.AreaId = area.Id;
            this.$webAccess.UpdateAreaDetails(request).pipe(
                map(response => response.Result as any),
                map(result => this.objectToModel(result)),
                map(response => response as Area))
                .subscribe(_area => {
                    sub.next(_area);
                });
        } else {
            this.$webAccess.AddAreaDetails(request).pipe(
                map(response => response.Result as any),
                map(result => this.objectToModel(result)),
                map(response => response as Area))
                .subscribe(_area => {
                    sub.next(_area);
                });
        }

        return sub.asObservable();
    }

    private objectToModel(result: any): any {
        switch (result.Tp) {
            case 5:
                result.Tp = 1;
                break;
            case 3:
                result.Tp = 2;
                break;
            default:
                result.Tp = 3;
                break;
        }
        result.Coords = (result.P || []).map((p: any) =>
            <Point>{
                Lat: p.Lat.toString().replace(',', '.'),
                Lon: p.Lon.toString().replace(',', '.')
            });

        return result;
    }
}
